"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BillingClient = void 0;
const commons_logs_1 = require("@deliverr/commons-logs");
const base_client_1 = require("@deliverr/base-client");
const client_auth_1 = require("@deliverr/client-auth");
const core_auth_1 = require("@deliverr/core-auth");
const billing_types_1 = require("@deliverr/billing-types");
const lodash_1 = require("lodash");
class BillingClient extends base_client_1.ApiClient {
    constructor(config) {
        const BILLING_SERVICE_URL = process.env.BILLING_SERVICE_URL || config.baseURL;
        if (!BILLING_SERVICE_URL) {
            commons_logs_1.log.warn("BILLING_SERVICE_URL environment variable is not set");
        }
        let apiClientConfig = {
            authEngine: core_auth_1.AuthEngine.COGNITO,
        };
        if (config.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
            apiClientConfig = {
                authEngine: core_auth_1.AuthEngine.COGNITO_INTERNAL,
                tokenRefreshFn: tokenHandler.getTokenFn(),
            };
        }
        apiClientConfig = {
            ...config,
            ...apiClientConfig,
            baseURL: `${BILLING_SERVICE_URL}`,
        };
        super(apiClientConfig);
    }
    async getSimpleInvoiceList(offset, limit) {
        return this.get("/invoice/v1/list/simple", { limit, offset });
    }
    async getInvoiceAsCSV(chargebeeInvoiceId) {
        return this.get(`/invoice/v1/details/csv/${chargebeeInvoiceId}`);
    }
    async reprocessS3Invoice(chargebeeInvoiceId) {
        return this.put(`/invoice/v1/details/reprocess-csv/${chargebeeInvoiceId}`, {});
    }
    async getInvoiceAsSignedUrl(chargebeeInvoiceId) {
        return this.get(`/invoice/v1/details/external-url/${chargebeeInvoiceId}`);
    }
    async getInvoiceAsPDF(chargebeeInvoiceId) {
        return this.get(`/invoice/v1/details/pdf/${chargebeeInvoiceId}`);
    }
    async getCreditNoteAsPDF(creditNoteId) {
        return this.get(`/credit-note/v1/pdf/${creditNoteId}`);
    }
    async createPromotion(promotion) {
        return this.post("/PROMO/v1/billing/promotion/create", promotion);
    }
    async applyPromotion(sellerId, code) {
        return this.post("/PROMO/v1/billing/promotion/apply", {}, { code, seller: sellerId });
    }
    async listSellerPromotions(sellerId) {
        return this.get(`/PROMO/v1/billing/promotion/${sellerId}`);
    }
    async cancelPromotion(sellerId, code) {
        return this.delete(`/PROMO/v1/billing/promotion/cancel`, {
            code,
            seller: sellerId,
        });
    }
    async addManualCharge(manualCharge) {
        return this.post(`/manual-action/v1/billing/add-charge`, manualCharge);
    }
    async addManualRefund(manualRefund) {
        return this.post(`/manual-action/v1/billing/add-refund`, manualRefund);
    }
    async addManualCredit(manualCredit) {
        return this.post(`/manual-action/v1/billing/add-credit`, manualCredit);
    }
    async getSellerBillingCycle(sellerId) {
        return this.put(`/seller-billing-cycle/v1/billing/sellerId/${sellerId}`, {});
    }
    async updateSellerBillingCycleLength(sellerId, cycleLength) {
        return this.get(`/seller-billing-cycle/v1/billing/sellerId/${sellerId}/cycle-length/${cycleLength}`);
    }
    async deactivateSellerBillingAccount(sellerId) {
        return this.put(`/seller-billing-account/v1/${sellerId}/deactivate`, {});
    }
    async getBillingSummary(sellerId) {
        return this.get(`/invoice/v1/billing/summary/${sellerId}`);
    }
    async getInvoiceHistory(sellerId, limit, offset) {
        return this.get(`/invoice/v1/billing/history/${sellerId}`, { limit, offset });
    }
    async getInvoiceCount(sellerId) {
        return this.get(`/invoice/v1/billing/count/${sellerId}`);
    }
    async getPrimaryPaymentSource(sellerId) {
        return this.get(`/invoice/v2/payment/sources/primary/${sellerId}`);
    }
    async getChargebeeSession() {
        return this.get(`/chargebee/v1/session`);
    }
    async addChargebeeContact(sellerId, contact) {
        return this.post(`/chargebee/v1/${sellerId}/contact`, contact);
    }
    async updateChargebeeContact(sellerId, contact) {
        return this.put(`/chargebee/v1/${sellerId}/contact`, contact);
    }
    async getChargebeeAccount(sellerId) {
        return this.get(`/chargebee/v1/${sellerId}`);
    }
    async getCreditsHistory(sellerId, limit, offset) {
        return this.get(`/credits/v1/billing/history/${sellerId}`, { limit, offset });
    }
    async createInternalCredit(sellerIds, credit) {
        return this.post("/v2/credits/internal", {
            sellerIds: sellerIds,
            credit: credit,
        });
    }
    async getContraRevenueItemsForInvoice(sellerId, invoiceId) {
        return this.get(`/contra-revenue/v1/${sellerId}/${invoiceId}`);
    }
    async offboardSeller(sellerId, hostKey) {
        return this.put(`/manual-action/v1/billing/offboard-seller/${sellerId}`, {}, { hostKey });
    }
    async createChargeInvoiceItem(chargeInvoiceItem, idempotencyKey) {
        return this.postWithHeaders("/v1/charge/item", {
            chargeItem: chargeInvoiceItem,
        }, {}, { "idempotency-key": idempotencyKey });
    }
    async batchCreateChargeInvoiceItems(chargeInvoiceItem, idempotencyKey) {
        return this.postWithHeaders("/v1/charge/items", {
            chargeItems: chargeInvoiceItem,
        }, {}, { "idempotency-key": idempotencyKey });
    }
    async getChargeInvoiceItems(query = {}, pageNumber, pageSize, orderBy, order) {
        return this.get("/v1/charge/item", {
            query: JSON.stringify(query),
            pageNumber,
            pageSize,
            orderBy,
            order,
        });
    }
    async createChargeInvoiceItemType(chargeInvoiceItemType, idempotencyKey) {
        return this.postWithHeaders(`/v1/charge/type/${chargeInvoiceItemType}`, {}, {}, { "idempotency-key": idempotencyKey });
    }
    async getChargeInvoiceItemTypes(includeInactive = true) {
        return this.get("/v1/charge/types", {
            includeInactive,
        });
    }
    async deactivateChargeInvoiceItemType(invoiceItemType) {
        return this.delete(`/v1/charge/type/${invoiceItemType}`);
    }
    async flushChargeInvoiceItemCache() {
        return this.put("/v1/charge/flush-charge-item-cache", {});
    }
    async flushChargeInvoiceItemTypeCache() {
        return this.put("/v1/charge/flush-charge-item-type-cache", {});
    }
    createOrderUpdates(shipmentUpdates) {
        return shipmentUpdates
            .map(({ sellerId, orderId }) => ({
            type: billing_types_1.BillingEventType.ORDER_SHIPPED,
            sellerId,
            orderId,
        }))
            .filter(({ sellerId, orderId }) => sellerId && orderId);
    }
    createRemovalShipmentUpdates(shipmentUpdates) {
        return shipmentUpdates
            .map(({ shipmentIds, orderId }) => ({
            type: billing_types_1.BillingEventType.REMOVAL_SHIPPED,
            shipmentIds,
            orderId,
        }))
            .filter(({ shipmentIds, orderId }) => shipmentIds && orderId);
    }
    createInboundUpdates(inboundUpdates) {
        return inboundUpdates.map((data) => ({
            ...data,
            type: billing_types_1.BillingEventType.INBOUND_CREATED,
        }));
    }
    async getChargeSummary(startDateTime, endDateTime, sellerId) {
        return this.get(`/v1/public-api/charge-summary`, { startDateTime, endDateTime }, this.getSellerIdHeader(sellerId));
    }
    async getSignedUrlDetails(yearMonth, sellerId) {
        return this.get(`/v1/public-api/monthly-details/${yearMonth}`, {}, this.getSellerIdHeader(sellerId));
    }
    async getChargeDetailsInDateRange(queryParams, sellerId) {
        return this.get(`/v1/public-api/charge-details`, queryParams, this.getSellerIdHeader(sellerId));
    }
    async getChargeTotalInDateRange(queryParams, sellerId) {
        return this.get(`/v1/public-api/charge-total`, queryParams, this.getSellerIdHeader(sellerId));
    }
    async generateMonthlyChargeDetailsReport(request) {
        return this.post(`/v1/public-api/generate-report`, request);
    }
    async getSellerDues(sellerId) {
        return this.get(`/api/v1/sellerDues/${sellerId}`);
    }
    getSellerIdHeader(sellerId) {
        return { "x-seller-id": sellerId };
    }
    getSellerConfig(sellerId) {
        return this.get(`/api/v1/sellerConfig/${sellerId}`);
    }
    createSellerConfig(sellerConfig) {
        return this.post(`/api/v1/sellerConfig`, sellerConfig);
    }
    updateSellerConfig(sellerConfig) {
        return this.put(`/api/v1/sellerConfig`, sellerConfig);
    }
    getSellerConfigHistory(sellerId, limit, offset) {
        if ((0, lodash_1.isEmpty)(sellerId)) {
            throw new Error(`Seller id can't be empty or null`);
        }
        const query = {};
        if (limit !== undefined) {
            query.limit = limit; // Add limit if defined
        }
        if (offset !== undefined) {
            query.offset = offset; // Add offset if defined
        }
        return this.get(`/api/v1/sellerConfig/${sellerId}/history`, query);
    }
    //MAX 150 orders are allowed in a single request
    getBulkOrderActualCosts(orderIds) {
        return this.post(`/api/v1/orders/bulk/actualCost`, orderIds);
    }
    getFulfillmentChargeBreakup(orderId) {
        return this.get(`/api/v1/orders/${orderId}/chargeBreakup`);
    }
    async hasBillableItem(sellerId) {
        return this.get(`/invoice/v1/billing/${sellerId}/hasBillableItem`);
    }
    async getChargebeeHostedPageForNewSubscription(sellerId, hostedPageRequest) {
        return this.post(`/chargebee/v1/hostedPage/${sellerId}/newCheckout`, hostedPageRequest);
    }
    async getUnbilledCharges(sellerId) {
        return this.get(`/api/v1/unbilledCharges/${sellerId}`);
    }
}
exports.BillingClient = BillingClient;
//# sourceMappingURL=BillingClient.js.map